import React, { useEffect, useState, useContext } from 'react';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';

import {
  db,
  auth
} from "../utils/firebase";

import { TokenContext } from "./tokenContext"; // import TokenContext
import { logout } from "../utils/firebase";
import { createPortal } from "react-dom";
import GlobalNavDrawer from "./GlobalNavDrawer";
import { useAuth } from './AuthContext';
import {
  JOB_BANK_ROLES,
  RoleDenied,
  redirectToJobBankLogin,
  useJobBankRole
} from './jobBankRole';
import './EmployerProfile.css';
import {
  getEmployerJobs
} from '../services/jobService';

const defaultProfile = {
  companyName: '',
  industry: '',
  website: '',
  companyDescription: '',

  contactName: '',
  contactTitle: '',
  email: '',
  phone: '',

  address: '',
  city: '',
  province: '',
  postalCode: '',

  employeeCount: '',
  hiringRegions: '',
  remoteAvailable: false,

  logoUrl: ''
};

export default function EmployerProfile() {
  const { balance, setBalance } = useContext(TokenContext);
  const { currentUser } = useAuth();
  const { roleLoading, hasRole } = useJobBankRole(currentUser);
  const [profile, setProfile] = useState(defaultProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState({
    jobsPosted: 0,
    activeJobs: 0,
    applications: 0
  });

  const [carveOutForm, setCarveOutForm] = useState({
    title: '',
    description: '',
    taskExamples: '',
    barriersSupported: '',
    hoursPerWeek: '',
    schedule: '',
    supervisionLevel: '',
    wage: '',
    idealCandidateProfile: '',
    status: 'Open'
  });

  const [savingCarveOut, setSavingCarveOut] = useState(false);
  const [coachMessage, setCoachMessage] = useState('');
  const [sendingCoachMessage, setSendingCoachMessage] = useState(false);

  useEffect(() => {
      let isMounted = true;
  
      const fetchTokens = async () => {
        if (!currentUser?.uid) return;
  
        const userRef = doc(db, "users", currentUser.uid);
  
        try {
          const userDoc = await getDoc(userRef);
  
          if (userDoc.exists() && isMounted) {
            setBalance(userDoc.data().tokens ?? 0);
          }
        } catch (error) {
          console.error("Error fetching token count:", error);
        }
      };
  
      fetchTokens();
  
      return () => {
        isMounted = false;
      };
    }, [currentUser, setBalance]);

  useEffect(() => {
    if (
      currentUser &&
      !roleLoading &&
      hasRole([JOB_BANK_ROLES.EMPLOYER])
    ) {
      loadProfile();
    }
  }, [currentUser, roleLoading, hasRole]);

  const loadProfile = async () => {

    try {

      const user = auth.currentUser;

      if (!user) {
        setLoading(false);
        return;
      }

      const employerRef = doc(
        db,
        'employers',
        user.uid
      );

      const snapshot = await getDoc(employerRef);

      if (snapshot.exists()) {

        setProfile({
          ...defaultProfile,
          ...snapshot.data()
        });

      }

      const jobs =
        await getEmployerJobs();

      const jobsPosted =
        jobs.length;

      const activeJobs =
        jobs.filter(
          job => job.active
        ).length;

      const applications =
        jobs.reduce(
          (sum, job) =>
            sum + (job.applications || 0),
          0
        );

      setStats({
        jobsPosted,
        activeJobs,
        applications
      });

    } catch (error) {

      console.error(
        'Error loading employer profile:',
        error
      );

    } finally {

      setLoading(false);

    }

  };

  const handleChange = (e) => {

    const {
      name,
      value,
      type,
      checked
    } = e.target;

    setProfile(prev => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : value
    }));

  };

  const updateCarveOutForm = (field, value) => {
    setCarveOutForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetCarveOutForm = () => {
    setCarveOutForm({
      title: '',
      description: '',
      taskExamples: '',
      barriersSupported: '',
      hoursPerWeek: '',
      schedule: '',
      supervisionLevel: '',
      wage: '',
      idealCandidateProfile: '',
      status: 'Open'
    });
  };

  const createJobCarveOut = async () => {
    const user = auth.currentUser;

    if (!user) {
      alert('You must be logged in.');
      return;
    }

    if (!carveOutForm.title.trim()) {
      alert('Please enter a title for this job carve-out.');
      return;
    }

    try {
      setSavingCarveOut(true);

      await addDoc(
        collection(db, 'jobCarveOuts'),
        {
          companyId: user.uid,
          companyName: profile.companyName || '',
          title: carveOutForm.title || '',
          description: carveOutForm.description || '',
          taskExamples: String(carveOutForm.taskExamples || '')
            .split('\n')
            .map(item => item.trim())
            .filter(Boolean),
          barriersSupported: String(carveOutForm.barriersSupported || '')
            .split('\n')
            .map(item => item.trim())
            .filter(Boolean),
          hoursPerWeek: carveOutForm.hoursPerWeek || '',
          schedule: carveOutForm.schedule || '',
          supervisionLevel: carveOutForm.supervisionLevel || '',
          wage: Number(carveOutForm.wage || 0),
          idealCandidateProfile: carveOutForm.idealCandidateProfile || '',
          status: carveOutForm.status || 'Open',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }
      );

      resetCarveOutForm();
      alert('Job carve-out created.');
    } catch (error) {
      console.error('Unable to create job carve-out:', error);
      alert('Unable to create job carve-out.');
    } finally {
      setSavingCarveOut(false);
    }
  };

  const sendMessageToCareerCoaches = async () => {
    const user = auth.currentUser;

    if (!user) {
      alert('You must be logged in.');
      return;
    }

    if (!coachMessage.trim()) {
      return;
    }

    try {
      setSendingCoachMessage(true);

      await addDoc(
        collection(db, 'publicProfileMessages'),
        {
          recipientType: 'Career Coach Team',
          recipientName: 'Career Coach Team',
          senderUid: user.uid,
          senderEmail: profile.email || user.email || '',
          senderRole: 'Employer',
          senderCompanyName: profile.companyName || '',
          message: coachMessage.trim(),
          status: 'New',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }
      );

      setCoachMessage('');
      alert('Message sent to the Career Coach team.');
    } catch (error) {
      console.error('Unable to message career coaches:', error);
      alert('Unable to send message.');
    } finally {
      setSendingCoachMessage(false);
    }
  };

  const saveProfile = async () => {

    try {

      setSaving(true);

      const user = auth.currentUser;

      if (!user) {
        return;
      }

      const employerRef = doc(
        db,
        'employers',
        user.uid
      );

      await setDoc(
        employerRef,
        {
          ...profile,
          updatedAt: serverTimestamp()
        },
        {
          merge: true
        }
      );

      alert(
        'Employer profile saved successfully.'
      );

    } catch (error) {

      console.error(error);

      alert(
        'Unable to save employer profile.'
      );

    } finally {

      setSaving(false);

    }

  };

  if (!currentUser) {
    redirectToJobBankLogin("/employer");
    return null;
  }

  if (roleLoading) {
    return (
      <div className="employerProfileLoading">
        Checking Job Bank role...
      </div>
    );
  }

  if (!hasRole([JOB_BANK_ROLES.EMPLOYER])) {
    return (
      <RoleDenied
        title="Employer Access Required"
        message="Only users with the Job Bank Role of Employer can access and edit the Employer Profile."
        requestedRole={JOB_BANK_ROLES.EMPLOYER}
        requestedAccess="Employer Profile"
        requestKey="employer-profile"
        sourcePath="/employer"
      />
    );
  }

  if (loading) {

    return (
      <div className="employerProfileLoading">
        Loading Employer Profile...
      </div>
    );

  }

  return (

    <div className="employerProfile">

      <div className="profileHeader">

        <h1>
          Employer Profile
        </h1>

        <p>
          Manage your company information,
          contact details, and hiring profile.
        </p>

      </div>

    <div className="companySummaryCard">

    <div className="companySummaryHeader">

        <div className="companySummaryIdentity">

        <div className="companyLogoPlaceholder">
            🏢
        </div>

        <div>

            <h2>
            {profile.companyName || 'Company Name'}
            </h2>

            <p>
            {profile.industry || 'Industry'}
            </p>

            <p>
            {profile.city && profile.province
                ? `${profile.city}, ${profile.province}`
                : 'Location'}
            </p>

        </div>

        </div>

        <div className="primaryContactSummary">

        <h4>
            Primary Contact
        </h4>

        <p>
            {profile.contactName || 'Contact Name'}
        </p>

        <p>
            {profile.email || 'Email Address'}
        </p>

        </div>

    </div>

    <div className="companyStats">

        <div className="companyStatCard">

        <span className="statNumber">
            {stats.jobsPosted}
        </span>

        <span className="statLabel">
            Jobs Posted
        </span>

        </div>

        <div className="companyStatCard">

        <span className="statNumber">
            {stats.activeJobs}
        </span>

        <span className="statLabel">
            Active Jobs
        </span>

        </div>

        <div className="companyStatCard">

        <span className="statNumber">
            {stats.applications}
        </span>

        <span className="statLabel">
            Applications
        </span>

        </div>

        <div className="companyStatCard">

        <span className="statNumber">
            {profile.employeeCount || '-'}
        </span>

        <span className="statLabel">
            Employees
        </span>

        </div>

    </div>

    </div>

      {/* COMPANY INFORMATION */}

      <section className="profileSection">

        <h2>
          Company Information
        </h2>

        <div className="profileGrid">

          <div className="formGroup">
            <label>
              Company Name
            </label>
            <input
              type="text"
              name="companyName"
              value={profile.companyName}
              onChange={handleChange}
            />
          </div>

          <div className="formGroup">
            <label>
              Industry
            </label>
            <input
              type="text"
              name="industry"
              value={profile.industry}
              onChange={handleChange}
            />
          </div>

          <div className="formGroup">
            <label>
              Website
            </label>
            <input
              type="text"
              name="website"
              value={profile.website}
              onChange={handleChange}
            />
          </div>

          <div className="formGroup">
            <label>
              Number of Employees
            </label>
            <input
              type="text"
              name="employeeCount"
              value={profile.employeeCount}
              onChange={handleChange}
            />
          </div>

        </div>

        <div className="formGroup">

          <label>
            Company Description
          </label>

          <textarea
            rows="5"
            name="companyDescription"
            value={profile.companyDescription}
            onChange={handleChange}
          />

        </div>

      </section>

      {/* CONTACT INFORMATION */}

      <section className="profileSection">

        <h2>
          Primary Contact
        </h2>

        <div className="profileGrid">

          <div className="formGroup">
            <label>
              Contact Name
            </label>
            <input
              type="text"
              name="contactName"
              value={profile.contactName}
              onChange={handleChange}
            />
          </div>

          <div className="formGroup">
            <label>
              Contact Title
            </label>
            <input
              type="text"
              name="contactTitle"
              value={profile.contactTitle}
              onChange={handleChange}
            />
          </div>

          <div className="formGroup">
            <label>
              Email
            </label>
            <input
              type="email"
              name="email"
              value={profile.email}
              onChange={handleChange}
            />
          </div>

          <div className="formGroup">
            <label>
              Phone
            </label>
            <input
              type="text"
              name="phone"
              value={profile.phone}
              onChange={handleChange}
            />
          </div>

        </div>

      </section>

      {/* ADDRESS */}

      <section className="profileSection">

        <h2>
          Company Address
        </h2>

        <div className="profileGrid">

          <div className="formGroup">
            <label>
              Street Address
            </label>
            <input
              type="text"
              name="address"
              value={profile.address}
              onChange={handleChange}
            />
          </div>

          <div className="formGroup">
            <label>
              City
            </label>
            <input
              type="text"
              name="city"
              value={profile.city}
              onChange={handleChange}
            />
          </div>

          <div className="formGroup">
            <label>
              Province
            </label>
            <input
              type="text"
              name="province"
              value={profile.province}
              onChange={handleChange}
            />
          </div>

          <div className="formGroup">
            <label>
              Postal Code
            </label>
            <input
              type="text"
              name="postalCode"
              value={profile.postalCode}
              onChange={handleChange}
            />
          </div>

        </div>

      </section>

      {/* HIRING INFORMATION */}

      <section className="profileSection">

        <h2>
          Hiring Information
        </h2>

        <div className="profileGrid">

          <div className="formGroup">
            <label>
              Hiring Regions
            </label>
            <input
              type="text"
              name="hiringRegions"
              value={profile.hiringRegions}
              onChange={handleChange}
            />
          </div>

          <div className="checkboxGroup">

            <input
              type="checkbox"
              name="remoteAvailable"
              checked={profile.remoteAvailable}
              onChange={handleChange}
            />

            <label>
              Remote Opportunities Available
            </label>

          </div>

        </div>

      </section>

      <section className="profileSection jobCarveOutEmployerSection">

        <h2>
          Job Carve-Out Opportunities
        </h2>

        <p className="jobCarveOutEmployerHelp">
          Create customized employment opportunities for individuals whose
          strengths may not fit a traditional job description, schedule,
          supervision model, or full-time role.
        </p>

        <div className="profileGrid">

          <div className="formGroup">
            <label>Carve-Out Title</label>
            <input
              type="text"
              value={carveOutForm.title}
              placeholder="Example: Document Digitization Assistant"
              onChange={(e) => updateCarveOutForm('title', e.target.value)}
            />
          </div>

          <div className="formGroup">
            <label>Hours Per Week</label>
            <input
              type="text"
              value={carveOutForm.hoursPerWeek}
              placeholder="Example: 10-15"
              onChange={(e) => updateCarveOutForm('hoursPerWeek', e.target.value)}
            />
          </div>

          <div className="formGroup">
            <label>Schedule</label>
            <input
              type="text"
              value={carveOutForm.schedule}
              placeholder="Example: Tue/Thu mornings"
              onChange={(e) => updateCarveOutForm('schedule', e.target.value)}
            />
          </div>

          <div className="formGroup">
            <label>Hourly Wage</label>
            <input
              type="number"
              min="0"
              step="0.25"
              value={carveOutForm.wage}
              placeholder="Example: 18.50"
              onChange={(e) => updateCarveOutForm('wage', e.target.value)}
            />
          </div>

          <div className="formGroup">
            <label>Supervision Level</label>
            <select
              value={carveOutForm.supervisionLevel}
              onChange={(e) => updateCarveOutForm('supervisionLevel', e.target.value)}
            >
              <option value="">Select one</option>
              <option>Independent after training</option>
              <option>Light check-ins</option>
              <option>Regular coaching</option>
              <option>Close supervision</option>
            </select>
          </div>

          <div className="formGroup">
            <label>Status</label>
            <select
              value={carveOutForm.status}
              onChange={(e) => updateCarveOutForm('status', e.target.value)}
            >
              <option>Open</option>
              <option>Draft</option>
              <option>Paused</option>
              <option>Filled</option>
            </select>
          </div>

        </div>

        <div className="formGroup">
          <label>Description</label>
          <textarea
            value={carveOutForm.description}
            placeholder="Describe the customized role and the business need it supports."
            onChange={(e) => updateCarveOutForm('description', e.target.value)}
          />
        </div>

        <div className="formGroup">
          <label>Example Tasks — one per line</label>
          <textarea
            value={carveOutForm.taskExamples}
            placeholder={'Scan archived documents\nRename files using a naming convention\nOrganize digital folders'}
            onChange={(e) => updateCarveOutForm('taskExamples', e.target.value)}
          />
        </div>

        <div className="formGroup">
          <label>Barriers Supported — one per line</label>
          <textarea
            value={carveOutForm.barriersSupported}
            placeholder={'Autism spectrum\nAISH hour limitations\nTransportation barriers\nAnxiety or sensory needs'}
            onChange={(e) => updateCarveOutForm('barriersSupported', e.target.value)}
          />
        </div>

        <div className="formGroup">
          <label>Ideal Candidate Profile</label>
          <textarea
            value={carveOutForm.idealCandidateProfile}
            placeholder="Describe strengths, preferences, and support conditions that would help a candidate succeed."
            onChange={(e) => updateCarveOutForm('idealCandidateProfile', e.target.value)}
          />
        </div>

        <div className="saveSection">
          <button
            type="button"
            className="saveEmployerProfileButton"
            onClick={createJobCarveOut}
            disabled={savingCarveOut}
          >
            {savingCarveOut
              ? 'Creating...'
              : 'Create Job Carve-Out'}
          </button>
        </div>

      </section>

      <section className="profileSection employerCoachMessageSection">

        <h2>
          Message Career Coaches
        </h2>

        <p className="jobCarveOutEmployerHelp">
          Employers can message Career Coaches directly. Career Coaches coordinate
          candidate introductions and client profile access.
        </p>

        <div className="formGroup">
          <label>Message</label>
          <textarea
            value={coachMessage}
            placeholder="Write a note to the Career Coach team..."
            onChange={(e) => setCoachMessage(e.target.value)}
          />
        </div>

        <div className="saveSection">
          <button
            type="button"
            className="saveEmployerProfileButton"
            onClick={sendMessageToCareerCoaches}
            disabled={sendingCoachMessage || !coachMessage.trim()}
          >
            {sendingCoachMessage ? 'Sending...' : 'Send Message to Career Coaches'}
          </button>
        </div>

      </section>

      <div className="saveSection">

        <button
          className="saveEmployerProfileButton"
          onClick={saveProfile}
          disabled={saving}
        >
          {
            saving
              ? 'Saving...'
              : 'Save Employer Profile'
          }
        </button>

      </div>
          {createPortal(
            <GlobalNavDrawer
              hideCurrent
              links={[
                { to: "/purchase", label: `Tokens: ${balance}`},
                { to: "/jobbank", label: "Job Bank Home"},
                { to: "/voicebot", label: "iSpeakWell" },
                { to: "/resumerevisor", label: "Resumes" },
                { to: "/conversationpractice", label: "Convos" },
                { to: "/interview-practice", label: "Interviews" },
                { to: "/termsofservice", label: "Terms" },
                { to: "/privacypolicy", label: "Privacy" },
                { to: "/admin", label: "Admin" },
                { to: "/login", label: "Logout", onClick: async () => { await logout(); } },
              ]}
            />,
              document.body
          )}
    </div>

  );
}